import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDate, formatStatus, formatINRFull } from '@/lib/utils';
import { Plus, ArrowLeft, FileCheck, Download, Edit2, Package, FileText, ClipboardCheck, Archive, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HandoverPage() {
  const { user } = useAuth();
  const [handovers, setHandovers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchHandovers = () => {
    api.get('/handover').then(r => setHandovers(r.data.handovers || [])).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchHandovers(); api.get('/projects').then(r => setProjects(r.data.projects || [])); }, []);

  const viewHandover = async (id) => {
    const res = await api.get(`/handover/${id}`);
    setSelected(res.data.handover);
  };

  if (selected) {
    return <HandoverDetail handover={selected} onBack={() => setSelected(null)} onRefresh={() => viewHandover(selected.id)} />;
  }

  return (
    <div data-testid="handover-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Project Handover</h1>
          <p className="text-sm text-slate-500">{handovers.length} handover reports</p>
        </div>
        {['DIRECTOR', 'SALES'].includes(user?.role) && (
          <button onClick={() => setShowCreate(true)} data-testid="create-handover-btn"
            className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
            <Plus size={16} /> New Handover
          </button>
        )}
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {handovers.map(h => (
            <div key={h.id} onClick={() => viewHandover(h.id)} data-testid={`handover-card-${h.id}`}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm cursor-pointer transition-all">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-xs font-mono text-[#1B5486] font-medium">{h.project_code}</p>
                  <h3 className="text-sm font-semibold text-slate-900">{h.project_name}</h3>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${h.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : h.status === 'DRAFT' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>{h.status}</span>
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <div>Handover: {formatDate(h.handover_date)}</div>
                {h.commissioning_date && <div>Commissioning: {formatDate(h.commissioning_date)}</div>}
                <div>Warranty: {h.warranty_period}</div>
              </div>
            </div>
          ))}
          {handovers.length === 0 && <div className="col-span-3 text-center py-12 text-sm text-slate-400">No handover reports yet</div>}
        </div>
      )}

      {showCreate && <CreateHandoverModal projects={projects} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchHandovers(); }} />}
    </div>
  );
}

function HandoverDetail({ handover, onBack, onRefresh }) {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [tab, setTab] = useState('details');
  const [tcData, setTcData] = useState([]);
  const [boqItems, setBoqItems] = useState([]);
  const [workCompletion, setWorkCompletion] = useState(null);
  const [downloading, setDownloading] = useState('');

  const projectId = handover.project_id;

  const fetchTC = () => {
    api.get(`/handover/tc-checklist/${projectId}`).then(r => setTcData(r.data.items || [])).catch(console.error);
  };

  const fetchBOQ = () => {
    api.get(`/projects/${projectId}/boq`).then(r => setBoqItems(r.data.items || [])).catch(console.error);
  };

  const fetchWorkCompletion = () => {
    api.get(`/handover/work-completion/${projectId}`).then(r => setWorkCompletion(r.data)).catch(console.error);
  };

  useEffect(() => { fetchBOQ(); fetchTC(); fetchWorkCompletion(); }, []);

  const startEdit = () => { setEditForm({...handover}); setEditMode(true); };
  const saveEdit = async () => {
    await api.patch(`/handover/${handover.id}`, editForm);
    setEditMode(false);
    onRefresh();
  };

  const downloadFile = async (type) => {
    setDownloading(type);
    try {
      const endpoints = {
        'summary': `/handover/generate/summary-pdf/${projectId}`,
        'equipment': `/handover/generate/equipment-excel/${projectId}`,
        'work-completion': `/handover/generate/work-completion-pdf/${projectId}`,
        'tc': `/handover/generate/tc-pdf/${projectId}`,
        'zip': `/handover/generate/handover-zip/${projectId}`,
      };
      const res = await api.get(endpoints[type], { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const exts = { 'summary': 'pdf', 'equipment': 'xlsx', 'work-completion': 'pdf', 'tc': 'pdf', 'zip': 'zip' };
      a.download = `${type === 'zip' ? 'Handover_Package' : type}_${handover.project_code}.${exts[type]}`;
      a.click();
    } catch (err) { alert('Download failed'); console.error(err); }
    setDownloading('');
  };

  const f = editMode ? editForm : handover;

  const tabs = [
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'tc', label: 'Test & Commission', icon: ClipboardCheck },
    { id: 'work', label: 'Work Summary', icon: CheckCircle },
    { id: 'documents', label: 'Documents', icon: Archive },
  ];

  return (
    <div data-testid="handover-detail" className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-400 hover:text-slate-600"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-slate-900 font-heading">{handover.project_code} - Handover</h1>
          <p className="text-sm text-slate-500">{handover.project_name}</p>
        </div>
        {user?.role === 'DIRECTOR' && !editMode && tab === 'details' && <button onClick={startEdit} className="text-sm text-[#1B5486] hover:underline flex items-center gap-1"><Edit2 size={14} /> Edit</button>}
        {editMode && (
          <div className="flex gap-2">
            <button onClick={() => setEditMode(false)} className="text-sm border border-slate-200 px-3 py-1 rounded">Cancel</button>
            <button onClick={saveEdit} className="text-sm bg-[#1B5486] text-white px-3 py-1 rounded">Save</button>
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`hnd-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
              tab === t.id ? 'border-[#1B5486] text-[#1B5486]' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'details' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Handover Details</h3>
            <div className="space-y-2 text-sm">
              <DetailRow label="Handover Date" value={formatDate(f.handover_date)} editing={editMode} editComponent={<input type="date" value={f.handover_date||''} onChange={e => setEditForm({...f, handover_date: e.target.value})} className="input-field" />} />
              <DetailRow label="Commissioning Date" value={formatDate(f.commissioning_date)} editing={editMode} editComponent={<input type="date" value={f.commissioning_date||''} onChange={e => setEditForm({...f, commissioning_date: e.target.value})} className="input-field" />} />
              <DetailRow label="Status" value={f.status} editing={editMode} editComponent={<select value={f.status} onChange={e => setEditForm({...f, status: e.target.value})} className="input-field">{['DRAFT','IN_PROGRESS','COMPLETED'].map(s => <option key={s}>{s}</option>)}</select>} />
              <DetailRow label="Handed Over To" value={f.handed_over_to} editing={editMode} editComponent={<input value={f.handed_over_to||''} onChange={e => setEditForm({...f, handed_over_to: e.target.value})} className="input-field" />} />
              <DetailRow label="Received By" value={f.received_by} editing={editMode} editComponent={<input value={f.received_by||''} onChange={e => setEditForm({...f, received_by: e.target.value})} className="input-field" />} />
              <DetailRow label="Warranty" value={f.warranty_period} editing={editMode} editComponent={<input value={f.warranty_period||''} onChange={e => setEditForm({...f, warranty_period: e.target.value})} className="input-field" />} />
              <DetailRow label="Client Sign-off" value={f.client_sign_off ? 'Yes' : 'Pending'} />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">System Description</h3>
            {editMode ? (
              <textarea value={f.system_description||''} onChange={e => setEditForm({...f, system_description: e.target.value})} rows={5} className="input-field w-full" />
            ) : (
              <p className="text-sm text-slate-700">{f.system_description || 'No description provided'}</p>
            )}
            {f.notes && <><h4 className="text-xs font-semibold text-slate-500 mt-3">Notes</h4><p className="text-sm text-slate-700">{f.notes}</p></>}
          </div>
        </div>
      )}

      {tab === 'tc' && (
        <TCChecklist projectId={projectId} boqItems={boqItems} tcData={tcData} onRefresh={fetchTC} />
      )}

      {tab === 'work' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="work-completion-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-600" /> Work Completion Summary
          </h3>
          {workCompletion ? (
            <>
              <p className="text-xs text-slate-500 mb-3">{workCompletion.total_dwrs} DWRs aggregated</p>
              {workCompletion.summary.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">S.No</th>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Task</th>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Item</th>
                      <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Total Quantity</th>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">UOM</th>
                    </tr></thead>
                    <tbody>
                      {workCompletion.summary.map((w, idx) => (
                        <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-2 px-3 text-slate-500">{idx + 1}</td>
                          <td className="py-2 px-3 font-medium">{w.task_name}</td>
                          <td className="py-2 px-3">{w.item}</td>
                          <td className="py-2 px-3 text-right font-mono font-medium">{w.total_quantity.toLocaleString('en-IN', { maximumFractionDigits: 1 })}</td>
                          <td className="py-2 px-3 text-slate-500">{w.uom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-slate-400">No DWR work summaries found</div>
              )}
            </>
          ) : (
            <div className="flex justify-center py-8"><div className="animate-spin w-6 h-6 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>
          )}
        </div>
      )}

      {tab === 'documents' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="documents-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Archive size={16} className="text-[#1B5486]" /> Handover Documents
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            <DocCard title="Project Summary" desc="Project details & info" icon={FileText} color="bg-blue-50 text-blue-700"
              onDownload={() => downloadFile('summary')} loading={downloading === 'summary'} testId="dl-summary" />
            <DocCard title="Equipment Schedule" desc="BOQ items with serial numbers" icon={Package} color="bg-purple-50 text-purple-700"
              onDownload={() => downloadFile('equipment')} loading={downloading === 'equipment'} testId="dl-equipment" />
            <DocCard title="Work Completion" desc="Aggregated DWR work summary" icon={CheckCircle} color="bg-emerald-50 text-emerald-700"
              onDownload={() => downloadFile('work-completion')} loading={downloading === 'work-completion'} testId="dl-work-completion" />
            <DocCard title="Test & Commissioning" desc="T&C checklist with results" icon={ClipboardCheck} color="bg-amber-50 text-amber-700"
              onDownload={() => downloadFile('tc')} loading={downloading === 'tc'} testId="dl-tc" />
          </div>

          <div className="border-t border-slate-200 pt-4">
            <button onClick={() => downloadFile('zip')} disabled={downloading === 'zip'} data-testid="dl-handover-zip"
              className="bg-[#1B5486] text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-[#144066] flex items-center gap-2 disabled:opacity-60 transition-all">
              {downloading === 'zip' ? (
                <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Generating Package...</>
              ) : (
                <><Download size={16} /> Download Handover Package (ZIP)</>
              )}
            </button>
            <p className="text-xs text-slate-500 mt-2">Includes: Summary.pdf, Equipment_Schedule.xlsx, Work_Completion.pdf, Test_Commissioning.pdf, Handover_Certificate.pdf</p>
          </div>
        </div>
      )}
    </div>
  );
}

function DocCard({ title, desc, icon: Icon, color, onDownload, loading, testId }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 flex items-center gap-3 hover:shadow-sm transition-all">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
      <button onClick={onDownload} disabled={loading} data-testid={testId}
        className="text-[#1B5486] hover:text-[#144066] disabled:opacity-50 flex-shrink-0">
        {loading ? <div className="animate-spin w-4 h-4 border-2 border-[#1B5486] border-t-transparent rounded-full" /> : <Download size={16} />}
      </button>
    </div>
  );
}

function TCChecklist({ projectId, boqItems, tcData, onRefresh }) {
  const [checklist, setChecklist] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const tcMap = {};
    for (const tc of tcData) {
      tcMap[tc.boq_item_id] = tc;
    }
    setChecklist(boqItems.map(item => ({
      boq_item_id: item.id,
      description: item.description,
      qty: item.quoted_qty,
      tested: tcMap[item.id]?.tested || false,
      passed: tcMap[item.id]?.passed || false,
      remarks: tcMap[item.id]?.remarks || '',
      date: tcMap[item.id]?.date || '',
    })));
  }, [boqItems, tcData]);

  const updateItem = (idx, field, value) => {
    const updated = [...checklist];
    updated[idx] = { ...updated[idx], [field]: value };
    setChecklist(updated);
  };

  const saveChecklist = async () => {
    setSaving(true);
    try {
      await api.post(`/handover/tc-checklist/${projectId}`, {
        items: checklist.map(c => ({
          boq_item_id: c.boq_item_id,
          tested: c.tested,
          passed: c.passed,
          remarks: c.remarks,
          date: c.date,
        }))
      });
      onRefresh();
    } catch (err) { alert('Save failed'); }
    setSaving(false);
  };

  const testedCount = checklist.filter(c => c.tested).length;
  const passedCount = checklist.filter(c => c.passed).length;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="tc-checklist-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <ClipboardCheck size={16} className="text-amber-600" /> Test & Commissioning Checklist
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">{testedCount}/{checklist.length} Tested | {passedCount}/{checklist.length} Passed</span>
          <button onClick={saveChecklist} disabled={saving} data-testid="save-tc-btn"
            className="bg-[#1B5486] text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-[#144066] disabled:opacity-60">
            {saving ? 'Saving...' : 'Save Checklist'}
          </button>
        </div>
      </div>

      {checklist.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">No BOQ items found for this project</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 bg-slate-50/50">
              <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500 w-8">S.No</th>
              <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Description</th>
              <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-slate-500 w-12">Qty</th>
              <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-slate-500 w-16">Tested</th>
              <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-slate-500 w-16">Passed</th>
              <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500 w-28">Date</th>
              <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Remarks</th>
            </tr></thead>
            <tbody>
              {checklist.map((item, idx) => (
                <tr key={item.boq_item_id} className={`border-b border-slate-50 ${item.tested && item.passed ? 'bg-green-50/30' : ''}`} data-testid={`tc-row-${item.boq_item_id}`}>
                  <td className="py-2 px-2 text-slate-500">{idx + 1}</td>
                  <td className="py-2 px-2 text-slate-900">{item.description}</td>
                  <td className="py-2 px-2 text-center">{item.qty}</td>
                  <td className="py-2 px-2 text-center">
                    <input type="checkbox" checked={item.tested} onChange={e => updateItem(idx, 'tested', e.target.checked)}
                      className="w-4 h-4 text-[#1B5486] rounded border-slate-300" data-testid={`tc-tested-${idx}`} />
                  </td>
                  <td className="py-2 px-2 text-center">
                    <input type="checkbox" checked={item.passed} onChange={e => updateItem(idx, 'passed', e.target.checked)}
                      className="w-4 h-4 text-emerald-600 rounded border-slate-300" data-testid={`tc-passed-${idx}`} />
                  </td>
                  <td className="py-2 px-2">
                    <input type="date" value={item.date} onChange={e => updateItem(idx, 'date', e.target.value)}
                      className="input-field w-full text-xs py-1" />
                  </td>
                  <td className="py-2 px-2">
                    <input value={item.remarks} onChange={e => updateItem(idx, 'remarks', e.target.value)}
                      placeholder="Remarks..." className="input-field w-full text-xs py-1" data-testid={`tc-remarks-${idx}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, editing, editComponent }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-500">{label}</span>
      <span>{editing && editComponent ? editComponent : (value || '-')}</span>
    </div>
  );
}

function CreateHandoverModal({ projects, onClose, onCreated }) {
  const [form, setForm] = useState({
    project_id: '', handover_date: new Date().toISOString().slice(0, 10), commissioning_date: '',
    handed_over_to: '', received_by: '', system_description: '', warranty_period: '12 months',
    testing_report: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post('/handover', form); onCreated(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-handover-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">New Handover Report</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Project *</label>
            <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} required className="input-field w-full" data-testid="handover-project-select">
              <option value="">Select Project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
            </select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Handover Date</label>
              <input type="date" value={form.handover_date} onChange={e => setForm({...form, handover_date: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Commissioning Date</label>
              <input type="date" value={form.commissioning_date} onChange={e => setForm({...form, commissioning_date: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Handed Over To</label>
              <input value={form.handed_over_to} onChange={e => setForm({...form, handed_over_to: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Received By</label>
              <input value={form.received_by} onChange={e => setForm({...form, received_by: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">System Description</label>
            <textarea value={form.system_description} onChange={e => setForm({...form, system_description: e.target.value})} rows={3} className="input-field w-full" /></div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Warranty Period</label>
            <input value={form.warranty_period} onChange={e => setForm({...form, warranty_period: e.target.value})} className="input-field w-full" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-handover-btn" className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Creating...' : 'Create Handover'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
